// src/modules/app-views/products/service/aiService.js
// *****************************************************************************
// AI SERVICE - Product Cataloging
// *****************************************************************************

import axiosService from 'services/axiosService';
import financialService from 'services/financialService';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';

// *****************************************************************************
// CATALOG PRODUCTS
// *****************************************************************************

/**
 * Cataloga um produto usando IA
 * @param {Object} params - Parametros da catalogacao
 * @param {string} params.productDescription - Descricao do produto (required)
 * @param {number} params.price - Preco de venda (required)
 * @param {string} [params.supplierName] - Nome do fornecedor
 * @param {string} [params.supplierId] - ID do fornecedor existente
 * @param {string} [params.customerSite] - Site do cliente para busca de imagens
 * @param {string} [params.ean] - Codigo EAN/GTIN se conhecido
 * @param {string} [params.model] - Modelo de IA (default: claude-sonnet-4-20250514)
 * @param {string} [params.companyId] - ID da empresa para contexto fiscal
 * @param {number} [params.requestOrigin=0] - Origin of request (0: manual, 1: migrated, 2: automatic, 3: ia)
 * @returns {Promise<Object>} Dados do produto catalogado
 */
export const catalogProduct = async ({
  productDescription,
  price,
  supplierName,
  supplierId,
  customerSite,
  ean,
  model,
  companyId,
  requestOrigin = 0,
}) => {
  const [doc, result] = await axiosService.post({
    url: API.PRODUCTS.IA_CATALOG,
    data: {
      productDescription,
      price,
      supplierName,
      supplierId,
      customerSite,
      ean,
      model,
      companyId,
      requestOrigin,
    },
    returnAsObject: true,
    timeout: 120000, // 2 minutes - AI cataloging can take longer due to image search and upload
  });

  return result;
};

// *****************************************************************************
// PRICES
// *****************************************************************************

/**
 * Get AI model prices for product cataloging
 * @returns {Promise<Object>} List of models with prices
 */
export const getAiPrices = async () => {
  const [docs, result] = await axiosService.get({
    url: API.PRODUCTS.IA_PRICES,
  });

  return result;
};

// *****************************************************************************
// PROVIDERS
// *****************************************************************************

/**
 * Lista providers e modelos disponiveis
 * @returns {Promise<Object>} Lista de providers e modelos
 */
export const getProviders = async () => {
  const [docs, result] = await axiosService.get({
    url: API.AI.PROVIDERS,
  });

  return result;
};

// *****************************************************************************
// USAGE STATISTICS
// *****************************************************************************

/**
 * Obtem estatisticas de uso
 * @param {Object} params - Parametros do filtro
 * @param {string} [params.startDate] - Data inicial ISO
 * @param {string} [params.endDate] - Data final ISO
 * @param {string} [params.provider] - Filtrar por provider
 * @param {string} [params.operation] - Filtrar por operacao
 * @returns {Promise<Object>} Estatisticas de uso
 */
export const getUsageStats = async ({ startDate, endDate, provider, operation } = {}) => {
  const query = {};
  if (startDate) query.startDate = startDate;
  if (endDate) query.endDate = endDate;
  if (provider) query.provider = provider;
  if (operation) query.operation = operation;

  const url = mergeUrlParamsQuery({
    url: API.AI.USAGE,
    query,
  });

  const [docs, result] = await axiosService.get({ url });

  return result;
};

/**
 * Obtem logs de uso paginados
 * @param {Object} params - Parametros da paginacao
 * @param {number} [params.page=1] - Pagina
 * @param {number} [params.pageSize=10] - Itens por pagina
 * @param {string} [params.sort='-createdAt'] - Campo de ordenacao
 * @param {string} [params.search] - Busca textual
 * @returns {Promise<Object>} Logs de uso paginados
 */
export const getUsageLogs = async ({ page = 1, pageSize = 10, sort = '-createdAt', search } = {}) => {
  const query = { page, pageSize, sort };
  if (search) query.search = search;

  const url = mergeUrlParamsQuery({
    url: API.AI.USAGE_PAGINATION,
    query,
  });

  const [docs, result] = await axiosService.get({ url });

  return result;
};

// *****************************************************************************
// PURCHASE HISTORY
// *****************************************************************************

export const getPurchaseHistoryBySupplier = async ({ productId, supplierId, page = 1, size = 10 }) => {
  const url = mergeUrlParamsQuery({
    url: API.GOODS_RECEIPTS.PAGINATION,
    query: {
      page,
      size,
      sort: JSON.stringify([{ field: '_metadata.audit.createdAt', order: 'desc', index: 1 }]),
      filter: JSON.stringify({
        'items.linkedProductId': productId,
        'issuer.entityId': supplierId,
        status: 'confirmed',
      }),
    },
  });

  const [docs, result] = await axiosService.get({ url, returnAsObject: true });

  // Extract and process matching items from each goodsReceipt document
  const docsArray = result?.docs || result?.data || [];
  const history = [];

  for (const doc of docsArray) {
    const matchingItem = (doc.items || []).find(
      item => String(item.linkedProductId) === String(productId),
    );
    if (!matchingItem) continue;

    // Compute rateio (proportional accessory costs for this item)
    const productsAmount = doc.totals?.productsAmount || 0;
    const itemWeight = productsAmount > 0 ? (matchingItem.totalValue || 0) / productsAmount : 0;

    history.push({
      documentNumber: doc.nfeInfo?.number || doc.documentNumber || '-',
      entryDate: doc._metadata?.audit?.createdAt || doc.issueDate,
      quantity: matchingItem.quantity || 0,
      purchaseUnitUid: matchingItem.purchaseUnitUid,
      purchaseUnitName: matchingItem.purchaseUnitName || '',
      unitValue: matchingItem.unitValue || 0,
      conversionFactor: matchingItem.conversionFactor || 1,
      convertedUnitCost: matchingItem.convertedUnitCost || matchingItem.unitValue || 0,
      discountPercentage: matchingItem.discountPercentage || matchingItem.discount?.percentage || 0,
      discountValue: matchingItem.discountValue || matchingItem.discount?.value || 0,
      totalValue: matchingItem.totalValue || 0,
      freightApportionment: financialService.roundCurrency(itemWeight * (doc.totals?.freightAmount || 0)),
      insuranceApportionment: financialService.roundCurrency(itemWeight * (doc.totals?.insuranceAmount || 0)),
      otherExpensesApportionment: financialService.roundCurrency(itemWeight * (doc.totals?.otherExpensesAmount || 0)),
      goodsReceiptId: doc._id,
    });
  }

  return history;
};

// *****************************************************************************
// SUPPLIER MANAGEMENT
// *****************************************************************************

export const addProductSupplier = async ({ productId, supplierData }) => {
  // $push adds a new object or updates existing one (matched by _id in body)
  const url = mergeUrlParamsQuery({
    url: API.PRODUCTS.ADD_TO_SET,
    params: { _id: productId, operator: '$push', path: 'suppliers' },
  });

  const [result] = await axiosService.put({
    url,
    data: supplierData,
    headers: { 'x-resultasobject': true },
  });

  return result;
};

export const unsetAllPrimarySuppliers = async (productId, excludeId) => {
  // Unset isPrimary on all suppliers except the one being set as primary
  // Uses addToSet to update each supplier individually (suppliers array is small, typically 1-5)
  const url = mergeUrlParamsQuery({
    url: API.PRODUCTS.URL.replace(':_id?', productId),
  });

  // Fetch current product to get all suppliers
  const [product] = await axiosService.get({ url, returnAsObject: true });
  const suppliers = product?.suppliers || [];

  // Update each supplier that is currently primary (except the one being set)
  for (const supplier of suppliers) {
    if (supplier.isPrimary && String(supplier._id) !== String(excludeId)) {
      const updateUrl = mergeUrlParamsQuery({
        url: API.PRODUCTS.ADD_TO_SET,
        params: { _id: productId, operator: '$push', path: 'suppliers' },
      });
      await axiosService.put({
        url: updateUrl,
        data: { _id: supplier._id, isPrimary: false },
        headers: { 'x-resultasobject': true },
      });
    }
  }
};

export const removeProductSupplier = async ({ productId, subdocumentId }) => {
  // $pull removes the subdocument matching the _id
  const url = mergeUrlParamsQuery({
    url: API.PRODUCTS.ADD_TO_SET,
    params: { _id: productId, operator: '$pull', path: 'suppliers' },
  });

  const [result] = await axiosService.put({
    url,
    data: { _id: subdocumentId },
    headers: { 'x-resultasobject': true },
  });

  return result;
};

// *****************************************************************************
// EXPORT
// *****************************************************************************

export default {
  catalogProduct,
  getAiPrices,
  getProviders,
  getUsageStats,
  getUsageLogs,
  getPurchaseHistoryBySupplier,
  addProductSupplier,
  unsetAllPrimarySuppliers,
  removeProductSupplier,
};
